import { cva, type VariantProps } from "class-variance-authority"
import { AlertCircle, CheckCircle2, Info, TriangleAlert } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"

/*
   An alert names a consequence, not a condition. The `action` slot exists
   to make "what the reader does next" the default shape rather than an extra.

   Tone follows the palette's meaning, not the severity of the feeling:
   red = severity, amber = printed label / needs attention, blue = people
   and process, green = master / settled. An alert that picks a colour by
   how alarmed it is will collide with data that means something.
*/

const alert = cva(
  "flex gap-2.5 rounded-lg border px-3.5 py-3 text-body-sm leading-normal",
  {
    variants: {
      tone: {
        info: "border-info/25 bg-info-tint/60 text-info-strong",
        warning: "border-print/30 bg-print-tint/60 text-print-strong",
        error: "border-err/25 bg-err-tint/60 text-text",
        success: "border-master/25 bg-master-tint/60 text-text",
        neutral: "border-border bg-surface-sunken text-text-2",
      },
      size: {
        sm: "px-3 py-2.5 text-caption",
        md: "",
      },
    },
    defaultVariants: { tone: "neutral", size: "md" },
  },
)

const ICON = {
  info: Info,
  warning: TriangleAlert,
  error: AlertCircle,
  success: CheckCircle2,
  neutral: Info,
} as const

const ICON_TONE = {
  info: "text-info",
  warning: "text-print",
  error: "text-err",
  success: "text-master",
  neutral: "text-text-3",
} as const

export interface AlertProps
  extends React.ComponentPropsWithoutRef<"div">,
    VariantProps<typeof alert> {
  title?: React.ReactNode
  /** Buttons or links. What the reader does next. */
  action?: React.ReactNode
  /** Hide the icon when the alert sits inside something already marked. */
  bare?: boolean
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { className, tone = "neutral", size, title, action, bare, children, ...props },
  ref,
) {
  const Icon = ICON[tone ?? "neutral"]
  return (
    <div
      ref={ref}
      role={tone === "error" ? "alert" : "status"}
      className={cn(alert({ tone, size }), className)}
      {...props}
    >
      {!bare && (
        <Icon
          aria-hidden="true"
          className={cn("mt-0.5 size-4 shrink-0", ICON_TONE[tone ?? "neutral"])}
        />
      )}
      <div className="min-w-0 flex-1">
        {title && <div className="font-semibold">{title}</div>}
        {children && <div className={cn(title && "mt-1")}>{children}</div>}
        {action && <div className="mt-2.5 flex flex-wrap gap-2">{action}</div>}
      </div>
    </div>
  )
})

export { EmptyState, type EmptyStateProps, type EmptyReason } from "@/components/ui/empty-state"

/*
   A skeleton is only worth showing if it has the geometry of the thing
   that replaces it — otherwise it is a layout shift with extra steps.

   The base shape is a tinted rectangle with a highlight sweep. The
   sweep is a background-image gradient animated via background-position,
   so it composites cheaply and doesn't need `overflow:hidden` wrappers.
   `motion-reduce` collapses it to a static tint.

   `SkeletonText` varies row widths so a stack of rows doesn't read as a
   loading barcode. `SkeletonCircle` is provided because avatar placeholders
   are ubiquitous and shouldn't need `rounded-full` boilerplate every time.
*/

const skeletonBase =
  // Two-tone base: `--surface-sunken` fill + a soft highlight that sweeps across.
  // Using rgb() with alpha keeps it theme-agnostic without touching tokens.
  "relative overflow-hidden rounded-sm bg-surface-sunken " +
  "bg-[linear-gradient(90deg,transparent_0%,rgb(255_255_255/0.55)_50%,transparent_100%)] " +
  "dark:bg-[linear-gradient(90deg,transparent_0%,rgb(255_255_255/0.06)_50%,transparent_100%)] " +
  "bg-no-repeat bg-[length:200%_100%] bg-[position:-150%_0] " +
  "animate-shimmer motion-reduce:animate-none motion-reduce:bg-none"

export const Skeleton = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  function Skeleton({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        aria-hidden="true"
        className={cn(skeletonBase, className)}
        {...props}
      />
    )
  },
)

export const SkeletonCircle = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div">
>(function SkeletonCircle({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={cn(skeletonBase, "rounded-full", className)}
      {...props}
    />
  )
})

export interface SkeletonTextProps {
  lines?: number
  className?: string
}

export const SkeletonText = React.forwardRef<HTMLDivElement, SkeletonTextProps>(
  function SkeletonText({ lines = 3, className }, ref) {
    const widths = ["92%", "78%", "85%", "64%", "88%"]
    return (
      <div ref={ref} className={cn("flex flex-col gap-2", className)} aria-busy="true">
        {Array.from({ length: lines }, (_, i) => (
          <Skeleton key={i} className="h-3" style={{ width: widths[i % widths.length] }} />
        ))}
        <span className="sr-only">Loading</span>
      </div>
    )
  },
)
